import { useGetPatientBalancesSummaryQuery } from '@frontend/api-collections';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { CheckboxField, useControlledField, Text, TextLink } from '@frontend/design-system';
import { useBulkCollectionsFilterShallowStore } from '../../../store/bulk-collections-filter-store';
import { useBulkCollectionSelectAllShallowStore } from '../../../store/bulk-collections-select-all-store';
import { getFilterString } from '../utils';

type SelectAllCheckboxProps = {
  onCountClick: () => void;
};
export const SelectAllCheckbox = ({ onCountClick }: SelectAllCheckboxProps) => {
  const { t } = useTranslation('payments');
  const { selectedLocationIds } = useAppScopeStore();
  const filter = useBulkCollectionsFilterShallowStore('balanceType', 'searchValue');
  const { data, isLoading: isTotalCountLoading } = useGetPatientBalancesSummaryQuery({
    request: {
      locationIds: selectedLocationIds,
      filter: getFilterString(filter.balanceType, filter.searchValue),
    },
    options: {
      enabled: selectedLocationIds.length > 0,
      refetchOnMount: 'always',
    },
  });
  const totalSelectable = data?.totalBalancesWithPhoneNumbers ?? 0;
  const { isAllSelected, setIsAllSelected, setFilters, reset, selectedCount, excluded } =
    useBulkCollectionSelectAllShallowStore(
      'isAllSelected',
      'setIsAllSelected',
      'setFilters',
      'reset',
      'selectedCount',
      'excluded'
    );

  const handleChange = (val: boolean) => {
    setIsAllSelected(val, totalSelectable ?? 0);
    if (val) {
      setFilters({
        ...(filter?.balanceType?.length && { balanceType: filter.balanceType }),
        ...(filter?.searchValue && { searchValue: filter.searchValue }),
      });
    } else {
      reset();
    }
  };

  const selectProps = useControlledField({
    onChange: handleChange,
    type: 'checkbox',
    value: isAllSelected || !!selectedCount,
  });

  const isPartialSelection =
    selectedCount > 0 && !(isAllSelected && excluded.length === 0 && selectedCount === totalSelectable);

  return (
    <CheckboxField
      {...selectProps}
      name='select-all'
      label={
        !!selectedCount ? (
          <TextLink onClick={onCountClick} size='medium'>{`${selectedCount ?? 0} selected`}</TextLink>
        ) : (
          <Text size='medium'>{t('Select All')}</Text>
        )
      }
      indeterminate={isPartialSelection}
      disabled={isTotalCountLoading || (totalSelectable === 0 && selectedCount === 0)}
    />
  );
};
